import { DEFAULT_VOICE_TRIAL_DURATION_HOURS, MICRO_USD_PER_CREDIT } from '@/lib/billing/constants'
import type { PlanFormData } from './types'

import { createTranslator, type useTranslations } from "next-intl"
import englishCatalog from "@/translations/en/plan-catalog.json"

export type PlanCatalogTranslator = ReturnType<typeof useTranslations<"PlanCatalog">>
const englishTranslator = createTranslator({ locale: "en", messages: englishCatalog, namespace: "PlanCatalog" })

export type SectionId = 'overview' | 'credits' | 'voice' | 'resources' | 'shopify' | 'lifecycle' | 'pricing'
export type FieldKey = keyof PlanFormData
export type NumericKey = { [K in FieldKey]: PlanFormData[K] extends number | null ? K : never }[FieldKey]
export interface NumericField {
  section: SectionId
  key: NumericKey
  label: string
  hint: string
  unit?: string
  scale?: number
  min?: number
  max?: number
  step?: number
  empty?: string
  zero?: string
  valueLabel?: string
}

export const SECTIONS = getSECTIONS()

export function getSECTIONS(t: PlanCatalogTranslator = englishTranslator): { id: SectionId; title: string; detail: string; description: string }[] {
  return [
  {
    id: 'overview',
    title: t("overview"),
    detail: t("identityPresentation"),
    description: t("giveThisPlanAClearIdentityAndDecide"),
  },
  {
    id: 'pricing',
    title: t("pricing"),
    detail: t("subscriptionUsage"),
    description: t("setTheSubscriptionPriceConfigureItsPaymentProvider"),
  },
  {
    id: 'credits',
    title: t("creditsSpending"),
    detail: t("grantsTopUpsCaps"),
    description: t("separateWhatCustomersReceiveFromWhatTheyCan"),
  },
  {
    id: 'voice',
    title: t("voiceCampaigns"),
    detail: t("accessCapacity"),
    description: t("setVoiceAccessAndCapacityTogetherWithCampaign"),
  },
  {
    id: 'resources',
    title: t("usersIntegrations"),
    detail: t("usersKnowledgeCRM"),
    description: t("chooseTheResourcesAvailableToEachTenantOn"),
  },
  {
    id: 'shopify',
    title: t("shopifyAnalytics"),
    detail: t("storesInsights"),
    description: t("storeConnectivityAndAnalyticsAccessHaveSeparateLimits"),
  },
  {
    id: 'lifecycle',
    title: t("lifecycle"),
    detail: t("expiryGrace"),
    description: t("setWhenTenantAccessEndsAndHowLong"),
  },
]
}

export const FIELD_LABELS = getFIELD_LABELS()

export function getFIELD_LABELS(t: PlanCatalogTranslator = englishTranslator) {
  return {
  name: t("internalIdentifier"),
  display_name: t("planName"),
  status: t("visibility"),
  benefits: t("pricingCardBenefits"),
  billing_channel: t("paymentChannel"),
  shopify_plan_handle: t("shopifyPlanHandle"),
  voice_included: t("voiceIncluded"),
  voice_trial_enabled: t("voiceTrialAvailable"),
  campaigns_enabled: t("campaignAccess"),
  campaigns_trial_enabled: t("campaignsDuringSubscriptionTrial"),
  plan_credits_expire: t("planCreditsExpireWhenTheBillingPeriodEnds"),
  // History only.
  features: t("featureFlags"),
  billing_intervals: t("billingIntervals"),
} as const
}

export const NUMERIC_FIELDS = getNUMERIC_FIELDS()

export function getNUMERIC_FIELDS(t: PlanCatalogTranslator = englishTranslator) {
  return {
  price: {
    section: 'pricing',
    key: 'price_monthly_cents',
    label: t("monthlyPrice"),
    hint: t("theSubscriptionPriceCreditsAreConfiguredSeparately"),
    unit: 'USD / month',
    scale: 100,
    step: 0.01,
  },
  order: {
    section: 'overview',
    key: 'sort_order',
    label: t("displayOrder"),
    hint: t("lowerNumbersAppearFirst"),
  },
  initial: {
    section: 'credits',
    key: 'initial_balance_microusd',
    label: t("startingCredits"),
    hint: t("aOneTimeGrantWhenATenantIs"),
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
  },
  recurring: {
    section: 'credits',
    key: 'payment_to_balance_microusd',
    label: t("creditsPerPaidMonth"),
    hint: t("independentOfThePlanPriceAnnualPaymentsGrant"),
    unit: 'credits / month',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
  },
  payments: {
    section: 'credits',
    key: 'payment_to_balance_count',
    label: t("eligiblePayments"),
    hint: t("allPaymentsOrOnlyTheFirstNPayments"),
    min: 1,
    empty: t("everyPayment"),
    valueLabel: t("firstNPayments"),
  },
  topup: {
    section: 'credits',
    key: 'min_topup_cents',
    label: t("minimumCustomTopUp"),
    hint: t("minimumCustomStripePurchaseShopifyOffersItsOwn"),
    unit: 'USD',
    scale: 100,
    step: 0.01,
  },
  bonus: {
    section: 'credits',
    key: 'topup_bonus_percent',
    label: t("topUpBonus"),
    hint: t("extraCreditsAddedToAPurchasedTopUp"),
    unit: '%',
  },
  daily: {
    section: 'credits',
    key: 'daily_spending_limit_microusd',
    label: t("dailySpendingCap"),
    hint: t("aUsageCapNotALimitOnThe"),
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    empty: t("noCap"),
    zero: t("blockNewUsage"),
  },
  monthly: {
    section: 'credits',
    key: 'monthly_spending_limit_microusd',
    label: t("monthlySpendingCap"),
    hint: t("usageAlreadyInProgressCanCompleteAndCross"),
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    empty: t("noCap"),
    zero: t("blockNewUsage"),
  },
  agents: {
    section: 'voice',
    key: 'voice_agents_limit',
    label: t("voiceAgents"),
    hint: t("aFiniteCapacityWithVoiceAccessOrA"),
  },
  concurrency: {
    section: 'voice',
    key: 'voice_concurrency_limit',
    label: t("simultaneousCalls"),
    hint: t("voicePoolCapacityReservedForThisTenant"),
  },
  campaigns: {
    section: 'voice',
    key: 'campaigns_limit',
    label: t("campaignCount"),
    hint: t("retainedWhenCampaignAccessIsDisabled"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  numbers: {
    section: 'voice',
    key: 'pbx_numbers_limit',
    label: t("phoneNumbers"),
    hint: t("maximumActivePBXNumbers"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  flows: {
    section: 'voice',
    key: 'pbx_flows_limit',
    label: t("callRoutingFlows"),
    hint: t("maximumPBXRoutingConfigurations"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  users: {
    section: 'resources',
    key: 'users_limit',
    label: t("users"),
    hint: t("maximumUsersInATenant"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  knowledge: {
    section: 'resources',
    key: 'knowledge_bases_limit',
    label: t("knowledgeBaseCount"),
    hint: t("adminLimitNewPlansDefaultTo500Customer"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  size: {
    section: 'resources',
    key: 'knowledge_bases_size_mb_limit',
    label: t("knowledgeStorage"),
    hint: t("combinedSizeOfKnowledgeBases"),
    unit: 'MB',
    empty: t("unlimited"),
    zero: t("none"),
  },
  crm: {
    section: 'resources',
    key: 'crm_integrations_limit',
    label: t("cRMConnections"),
    hint: t("maximumConnectedCRMIntegrations"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  stores: {
    section: 'shopify',
    key: 'shopify_stores_limit',
    label: t("connectedStores"),
    hint: t("countsStoreDomainsAvailableOnEitherBillingChannel"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  channels: {
    section: 'shopify',
    key: 'marketing_channels_limit',
    label: t("marketingChannels"),
    hint: t("storedSettingCreationDoesNotCurrentlyEnforceThis"),
    empty: t("unlimited"),
    zero: t("none"),
  },
  insights: {
    section: 'shopify',
    key: 'shopify_insights_trial_duration_hours',
    label: t("insightsAccess"),
    hint: t("trialStartsOnFirstAccessOncePerTenant"),
    unit: 'hours',
    min: 1,
    empty: t("includedWithoutATimeLimit"),
    valueLabel: t("trialDuration"),
  },
  voiceDuration: {
    section: 'voice',
    key: 'voice_trial_duration_hours',
    label: t("voiceTrialDuration"),
    hint: t("durationHint", { hours: DEFAULT_VOICE_TRIAL_DURATION_HOURS }),
    unit: 'hours',
    empty: t("durationDefault", { hours: DEFAULT_VOICE_TRIAL_DURATION_HOURS, days: DEFAULT_VOICE_TRIAL_DURATION_HOURS / 24 }),
  },
  voiceBudget: {
    section: 'voice',
    key: 'voice_trial_budget_microusd',
    label: t("voiceTrialBudget"),
    hint: t("aCapOnWalletSpendingNotAnAdditional"),
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    empty: t("noBudgetCap"),
  },
  expiry: {
    section: 'lifecycle',
    key: 'expiry_days',
    label: t("accountAccessDuration"),
    hint: t("afterThisDurationTheTenantIsLockedAnd"),
    unit: 'days',
    min: 1,
    empty: t("noAccountExpiry"),
    valueLabel: t("expiresAfter"),
  },
  grace: {
    section: 'lifecycle',
    key: 'grace_period_days',
    label: t("failedPaymentGracePeriod"),
    hint: t("subscriptionGraceAfterAPaymentFailureNotExtra"),
    unit: 'days',
  },
  textMultiplier: {
    section: 'pricing',
    key: 'text_cost_multiplier',
    label: t("textCostMultiplier"),
    hint: t("alsoUsedForKnowledgeBaseEmbeddings"),
    min: 1,
    step: 0.1,
    unit: '× cost',
  },
  textMinimum: {
    section: 'pricing',
    key: 'text_min_charge_microusd',
    label: t("textMinimumCharge"),
    hint: t("perTextRequest"),
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    unit: 'credits',
  },
  textRounding: {
    section: 'pricing',
    key: 'text_rounding_decimals',
    label: t("textRounding"),
    hint: t("uSDDecimals21CreditSteps40"),
    max: 6,
  },
  voiceMultiplier: {
    section: 'pricing',
    key: 'voice_cost_multiplier',
    label: t("voiceCostMultiplier"),
    hint: t("appliedToVoiceProviderCosts"),
    min: 1,
    step: 0.1,
    unit: '× cost',
  },
  voiceMinimum: {
    section: 'pricing',
    key: 'voice_min_charge_microusd',
    label: t("voiceMinimumCharge"),
    hint: t("perVoiceCall"),
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    unit: 'credits',
  },
  voiceRounding: {
    section: 'pricing',
    key: 'voice_rounding_decimals',
    label: t("voiceRounding"),
    hint: t("uSDDecimalPrecisionFrom0To6"),
    max: 6,
  },
  embeddingMinimum: {
    section: 'pricing',
    key: 'embedding_min_charge_microusd',
    label: t("embeddingMinimumCharge"),
    hint: t("perKnowledgeBaseEmbeddingOperation"),
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    unit: 'credits',
  },
  embeddingRounding: {
    section: 'pricing',
    key: 'embedding_rounding_decimals',
    label: t("embeddingRounding"),
    hint: t("uSDDecimalPrecisionFrom0To6"),
    max: 6,
  },
} satisfies Record<string, NumericField>
}
